import { type FC, useEffect } from 'react'

import { EndCall } from '@liveHelper/assets/EndCall'
import { RingingTrackPlayer } from '@liveHelper/components/shared/RingingVideoContent'
import { RingingPhoneIcon } from '@liveHelper/components/shared/styles'
import {
  callDataSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { useTrackIncomingCallImpression } from '@liveHelper/tracking/useLiveHelperTracking'
import { useChatTheme } from '@oneToOne/shared/components/chat/theme/useChatTheme'
import { ActionButton } from '@oneToOne/shared/styles'
import type { IAgoraRTCRemoteUser } from 'agora-rtc-sdk-ng'

import { ScreenReaderAlert } from '@core/accessibility/screenReader/ScreenReaderAlert'
import { ImpressionContainer } from '@core/components/ImpressionContainer'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

import {
  AudioButtonWrapper,
  ChatHeaderRingingWrapper,
  ChatRingingButtonRow,
  RingingButton,
  VideoContainer,
} from './ChatHeaderRinging.styles'

interface Props {
  agentRTCUser: IAgoraRTCRemoteUser | undefined
}
/** Chat Incoming Audio Call component. */
export const ChatHeaderRinging: FC<Props> = ({ agentRTCUser }) => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const { theme } = useChatTheme()
  const { terminateCall, takeCall } = useStore(visitorPrivateActionsSelector)
  const callData = useStore(callDataSelector)
  const agentId = callData?.agentId
  const trackIncomingCallImpression = useTrackIncomingCallImpression()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        takeCall?.(agentId)
      } else if (event.key === 'Escape') {
        terminateCall?.({
          agentId,
          type: 'declineIncomingCall',
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [takeCall, agentId, terminateCall])

  return (
    <ChatHeaderRingingWrapper theme={theme}>
      <ImpressionContainer trackingFn={trackIncomingCallImpression} />
      <ScreenReaderAlert>
        {t(
          'oneToOne:There is an incoming call from from an agent. Press Control + Enter to accept the call or Escape to reject it.',
        )}
      </ScreenReaderAlert>
      <VideoContainer>
        <RingingTrackPlayer remoteUser={agentRTCUser} />
      </VideoContainer>
      <ChatRingingButtonRow theme={theme}>
        <AudioButtonWrapper>
          <RingingButton
            className={tw?.('text-sm')}
            aria-label={t(`oneToOne:Take Call`)}
            onClick={() => takeCall?.(agentId)}
            icon={
              <RingingPhoneIcon
                width="12px"
                fill="var(--white)"
                data-chromatic="ignore"
                label="ringing"
              />
            }
          >
            {t(`oneToOne:Audio call`)}
          </RingingButton>
        </AudioButtonWrapper>
        <ActionButton
          aria-label={t(`oneToOne:Reject Call`)}
          actionType="danger"
          buttonSize="sm"
          shape="square"
          onClick={() =>
            terminateCall?.({
              agentId,
              type: 'declineIncomingCall',
            })
          }
          icon={<EndCall width="16px" fill="white" label="end call" />}
        />
      </ChatRingingButtonRow>
    </ChatHeaderRingingWrapper>
  )
}
